<script setup lang="ts">
import { toRefs } from '@vueuse/core'
import type { ScheduleEvent, Team } from '~/types'

defineOptions({
  name: 'ScheduleEventsNextGameItem',
})

interface Props {
  scheduleEvent: ScheduleEvent.Model
  currentTeam: Team.Model
}

const props = defineProps<Props>()
const { scheduleEvent, currentTeam } = toRefs(props)
const {
  currentTeamInfo,
  hasScheduleEventBroadcast,
  hasScheduleEventScheduleViewLinks,
  id,
  isCurrentTeamAwayTeam,
  location,
  opponentTeamInfo,
  scheduleEventBroadcasts,
  scheduleEventScheduleViewLinks,
  sponsor,
  venue,
} = useScheduleEvent(scheduleEvent, currentTeam)
</script>

<template>
  <div class="next-game-content">
    <DashboardLink
      v-if="id"
      class="next-game-content__dashboard-link"
      :entity-id="id"
      entity-name="schedule-events"
    />
    <div class="next-game-content__teams">
      <ScheduleEventsSponsor
        v-if="sponsor"
        :sponsor
        class="schedule-event__sponsor"
      />
      <ScheduleEventsNextGameTeam
        v-if="currentTeamInfo"
        :team="currentTeamInfo"
        class="next-game-content__team"
        :class="{ 'next-game-content__team--away': isCurrentTeamAwayTeam }"
      />
      <div
        class="next-game-content__divider"
      >
        at
      </div>
      <ScheduleEventsNextGameTeam
        v-if="opponentTeamInfo"
        :team="opponentTeamInfo"
        class="next-game-content__team"
        :class="{ 'next-game-content__team--away': !isCurrentTeamAwayTeam }"
      />
    </div>
    <p
      v-if="venue && location"
      class="next-game-content__location"
    >
      {{ venue?.name }},
      {{ location }}
    </p>
    <div
      class="next-game-content__links"
    >
      <ScheduleEventsAllLinks
        v-if="hasScheduleEventScheduleViewLinks"
        :links="scheduleEventScheduleViewLinks"
      />
      <ScoresLink
        :schedule-event
      />
      <template v-if="hasScheduleEventBroadcast">
        <ScheduleEventsBroadcast :broadcasts="scheduleEventBroadcasts" />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.next-game-content {
    @apply w-full flex flex-col gap-[15px] items-start mt-[40px] relative;
    @apply xl:gap-[60px] xl:mt-[60px];

  &__teams {
    @apply flex flex-col gap-[10px] w-full;
    @apply lg:gap-[20px];
    @apply xl:gap-[15px];
  }

  &__team {
    @apply order-10;

    &--away {
      @apply order-[0];
    }
  }

  &__location {
    @apply text-[16px] text-primary/60;
  }

  &__divider {
    @apply ml-[65px] text-[16px] text-primary/70 leading-[1] order-1;
    @apply xl:ml-[80px];
  }

  &__links {
    @apply flex justify-center items-center w-full gap-[10px] flex-wrap;
    @apply md:justify-start;
  }

  &__thumb {
    @apply rounded-r-[10px] overflow-hidden relative hidden;
    @apply md:min-h-[480px] md:block;
    @apply xl:min-h-[550px];
  }

  &__image {
    @apply absolute left-0 top-0 w-full h-full object-cover;
  }

  &__dashboard-link {
    @apply absolute top-0 right-0 z-[2];
  }
}
</style>
